import {ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {
    EditingScreenshotEvent,
    InspectorEvent,
    ViewConfigPayload,
    WORKBENCH_VIEW_CONFIG,
    WorkbenchViewConfig
} from '../../shared/types';
import {LocalScreenshotId, ScreenshotId, ScreenshotDataUrl, ProjectId} from '@webmate-web-core/shared/types/ids';
import {WorkbenchViewComponent} from '../../shared/components/viewcontainers/workbench-view.component';
import {WorkbenchLayoutManager} from '../../shared/services/workbench-layout-manager.service';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {CanvasBasedImageComponent} from '@webmate-web-core/shared/components/screenshot/canvas-based-image.component';
import {switchMap, tap} from 'rxjs/operators';
import {WorkbenchCommandRegistry} from '../../shared/services/workbench-command-registry.service';
import {InspectorService} from '../../shared/services/inspector.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ImageEditorToolConfig} from '@webmate-web-core/shared/tools/screenshotviewer/image-editor.component';
import {WorkbenchContextService} from '../../shared/services/workbench-context.service';
import {SearchService} from '@webmate-web-core/shared/services/search.service';
import {faCompress, faExpand} from '@fortawesome/pro-solid-svg-icons';
import {isScrollableImage$} from '@webmate-web-core/shared/utils/image-scrollable';

export interface ScreenshotViewConfig extends ViewConfigPayload {
    data: { screenshotId?: ScreenshotId, localScreenshotId: LocalScreenshotId, screenshotName?: string };
}

@UntilDestroy()
@Component({
    templateUrl: './workbench-screenshot-viewer.component.html',
    styleUrls: ['./workbench-screenshot-viewer.component.less']
})
export class WorkbenchScreenshotViewerComponent extends WorkbenchViewComponent implements OnInit {

    @ViewChild(CanvasBasedImageComponent) imageComponent: CanvasBasedImageComponent;

    projectId: ProjectId;
    screenshotId?: ScreenshotId;
    localScreenshotId?: LocalScreenshotId;
    screenshotUrl?: ScreenshotDataUrl;
    screenshotName?: string;

    isActiveView: boolean = false;

    isImageEditActive: boolean = false;

    fitToContainer: boolean = false;
    shouldShowScrollViewBtn: boolean = false;

    fitWholeImage() {
       this.fitToContainer = true;
    }

    goToScrollView() {
        this.fitToContainer = false;
    }


    constructor(layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<ScreenshotViewConfig>,
                private imageService: ImageService,
                private workbenchCommandRegistry: WorkbenchCommandRegistry,
                private cdRef: ChangeDetectorRef,
                private readonly workbenchContextService: WorkbenchContextService,
                private searchService: SearchService,
                private inspectorService: InspectorService) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId()
        this.layoutManager.subscribeActiveViewId$().subscribe(activeViewId => {
            // By using the isActiveView flag in the @if, the canvas of the wm-image is re-rendered when the tab is activated
            // Otherwise the canvas dimensions are not properly updated
            // TODO heuristic to prevent unnecessary rerenders
            this.isActiveView = activeViewId === this.config.id;
            setTimeout(() => {
                // use set timeout to wait until the new active view has actually been rendered
                this.imageComponent?.refresh();
            })
        });
        this.screenshotId = this.config.payload?.data?.screenshotId;
        this.localScreenshotId = this.config.payload?.data?.localScreenshotId;
        if (this.localScreenshotId) {
            this.imageService.getLocalScreenshot(this.localScreenshotId).subscribe({
                next: (url) => {
                    this.screenshotUrl = url;
                    if (this.screenshotUrl) {
                        isScrollableImage$(this.screenshotUrl!).subscribe(isScrollable => {
                           this.shouldShowScrollViewBtn = isScrollable;
                        });
                    }
                    this.cdRef.markForCheck();
                },
                error: () => this.closeView()
            });
        } else if (this.screenshotId) {
            this.screenshotUrl = this.imageService.getImageUrl(this.screenshotId);
            isScrollableImage$(this.screenshotUrl).subscribe(isScrollable => {
                this.shouldShowScrollViewBtn = isScrollable;
            });
        }
        if (this.projectId && this.screenshotId) {
            this.searchService.getDeletedNotification(this.projectId).pipe(
                untilDestroyed(this),
                tap(id => {
                    if (this.screenshotId === id) {
                        this.closeView();
                    }
                })
            ).subscribe();
            this.searchService.getIndexingNotifications(this.projectId).pipe(
                untilDestroyed(this),
                switchMap(()=> {
                    return this.imageService.getImageMetadata(this.screenshotId);
                }),
                tap(metadata => {
                    this.layoutManager.changeViewName(this.config.id, metadata.name);
                    this.screenshotName = metadata.name;
                })
            ).subscribe();
            // Gets the name and sets it for the view
            this.imageService.getImageMetadata(this.screenshotId).subscribe(metadata => {
                this.layoutManager.changeViewName(this.config.id, metadata.name);
                this.screenshotName = metadata.name;
            })
        }
        this.layoutManager.subscribeActiveViewId$().pipe(
            untilDestroyed(this),
        ).subscribe(activeView => {
            const currentOpen= this.inspectorService.getCurrentInspectorItem();
            if (activeView === this.viewId && currentOpen !== 'Screenshot') {
                this.openScreenshotTools()
            }
            if (activeView !== this.viewId && currentOpen === 'Screenshot') {
                this.closeScreenshotTools()
            }
        });
    }

    override beforeViewClose() {
        if (this.localScreenshotId) {
            this.imageService.removeLocalScreenshot(this.localScreenshotId).subscribe();
        }
        this.closeScreenshotTools();
    }

    closeView() {
        this.layoutManager.closeView(this.config.id);
    }

    openScreenshotTools(){
        if (this.isActiveView){
            // Use setTimeout to make sure the current screenshot is only enabled after all previous have been disabled
            setTimeout(() => {
                this.workbenchCommandRegistry.executeCommand("screenshot:openScreenshotTools", {localId: this.localScreenshotId, id: this.screenshotId, name: this.screenshotName, inEditMode: this.isImageEditActive});
            }, 5)
        }
    }

    closeScreenshotTools(){
        const event: InspectorEvent = {type: "ClearInspector"};
        this.inspectorService.raiseInspectorEvent(event);
    }

    toggleEditMode() {
        this.isImageEditActive = !this.isImageEditActive;
        const event: EditingScreenshotEvent = {type: 'EditingScreenshot', editing: this.isImageEditActive};
        setTimeout(() => this.inspectorService.raiseInspectorEvent(event));
    }

    getImageEditorData(): ImageEditorToolConfig {
        if (!this.screenshotId) {
            console.error("WorkbenchScreenshotViewerComponent: Missing image id");
        }
        return new ImageEditorToolConfig(this.screenshotId!, this.screenshotUrl);
    }

    protected readonly faCompress = faCompress;
    protected readonly faExpand = faExpand;
}
